import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Link,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import "./App.css";
import { environment } from "./environment";
import {
  HomeIcon,
  UsersIcon,
  UserIcon,
  ClipboardIcon,
  Bars3Icon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarIcon,
  CheckCircleIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import Layout from "./Layout";
import Clients from "./Clients";
import ClientDetail from "./ClientDetail";
import Sessions from "./Sessions";
import SessionDetail from "./SessionDetail";
import EMRTypes from "./EMRTypes";
import EMRTypeDetails from "./EMRTypeDetails";
import Settings from "./Settings";
import ModalityDetail from "./ModalityDetail";
import ActivityDetail from "./ActivityDetail";
import { getOrCreateDeviceId } from "./utils";
import Reports from "./Reports";
import MultiSelectDropdown from "./components/MultiSelectDropdown";
import EMRTypeDocumentationPairs from "./components/EMRTypeDocumentationPairs";
import { api, typeWritingOptions } from "./utils/api";
import "./utils/logger"; // Initialize the logger system

const industryOptions = [
  "Technology",
  "Healthcare",
  "Finance",
  "Education",
  "Retail",
  "Energy",
  "Nonprofit",
  "Other",
];

// Placeholder components for each page
function AuthCard({ title, children }) {
  return (
    <div
      className="auth-card"
      style={{
        width: 370,
        padding: "0 1.3rem 1.5rem 1.3rem",
        borderRadius: 24,
        boxShadow: "0 4px 24px 0 rgba(60,72,100,0.09)",
        border: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
      }}
    >
      <img
        src="https://theicons.s3.us-east-2.amazonaws.com/icon128.png"
        alt="Noted logo"
        style={{
          width: 70,
          height: 50,
          margin: "2.1rem auto 1.1rem auto",
          display: "block",
          objectFit: "contain",
        }}
      />
      <h2
        style={{
          marginBottom: 22,
          fontWeight: 700,
          fontSize: 25,
          color: "#2d3a4a",
          textAlign: "center",
          letterSpacing: "-0.5px",
        }}
      >
        {title}
      </h2>
      {children}
    </div>
  );
}

const CenteredPage = ({ children }) => (
  <div
    style={{
      minHeight: "100vh",
      width: "100vw",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {children}
  </div>
);

const Login = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [resendStatus, setResendStatus] = useState("");
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResendStatus("");
    try {
      const deviceId = getOrCreateDeviceId();
      const res = await fetch(`${environment.apiUrl}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, deviceId }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data && (data.otp_required || data.otpRequired)) {
          console.log("Redirecting to OTP page...", form.email);
          navigate("/verify-otp", { state: { email: form.email } });
          return;
        }
        if (data && data.access_token) {
          setToken(data.access_token);
          const meRes = await fetch(`${environment.apiUrl}/me`, {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          });
          if (meRes.ok) {
            const userData = await meRes.json();
            setUser(userData);
          }
          navigate("/dashboard");
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || "An error occurred. Please try again.");
      }
    } catch (err) {
      setError("Network error");
    }
  };

  return (
    <CenteredPage>
      <AuthCard title="Sign in to Noted">
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <label
            htmlFor="login-email"
            style={{
              display: "block",
              width: "100%",
              marginBottom: 4,
              color: "#2d3a4a",
              fontWeight: 500,
              fontSize: 15,
            }}
          >
            Email
          </label>
          <input
            id="login-email"
            name="email"
            type="email"
            placeholder="Enter your email"
            value={form.email}
            onChange={handleChange}
            required
            style={{
              width: "100%",
              padding: "0.8rem 1rem",
              marginBottom: 16,
              border: "1px solid #dbeafe",
              borderRadius: 10,
              fontSize: 15,
              background: "#f7fafd",
              transition: "border 0.2s",
              boxSizing: "border-box",
            }}
          />
          <label
            htmlFor="login-password"
            style={{
              display: "block",
              width: "100%",
              marginBottom: 4,
              color: "#2d3a4a",
              fontWeight: 500,
              fontSize: 15,
            }}
          >
            Password
          </label>
          <input
            id="login-password"
            name="password"
            type="password"
            placeholder="Enter your password"
            value={form.password}
            onChange={handleChange}
            required
            style={{
              width: "100%",
              padding: "0.8rem 1rem",
              marginBottom: 20,
              border: "1px solid #dbeafe",
              borderRadius: 10,
              fontSize: 15,
              background: "#f7fafd",
              transition: "border 0.2s",
              boxSizing: "border-box",
            }}
          />
          <button
            type="submit"
            style={{
              width: "100%",
              padding: "0.85rem 0",
              background: "#4f8cff",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontSize: 16,
              fontWeight: 500,
              cursor: "pointer",
              marginBottom: 14,
              transition: "background 0.2s",
            }}
          >
            Login
          </button>
          {error && (
            <div className="auth-error">
              {error}
              {error === "Account not verified. Please check your email." && (
                <div className="mt-2 text-xs">
                  <button
                    type="button"
                    className="text-blue-600 underline hover:text-blue-800 ml-1"
                    onClick={async () => {
                      setResendStatus("");
                      try {
                        const res = await fetch(
                          `${environment.apiUrl}/auth/resend-verification`,
                          {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ email: form.email }),
                          }
                        );
                        if (res.ok) {
                          setResendStatus("Verification email sent!");
                        } else {
                          setResendStatus("Failed to send verification email.");
                        }
                      } catch {
                        setResendStatus("Failed to send verification email.");
                      }
                    }}
                  >
                    Resend verification email
                  </button>
                  {resendStatus && (
                    <div className="text-green-600 mt-1">{resendStatus}</div>
                  )}
                </div>
              )}
            </div>
          )}
        </form>
        <Link
          to="/forgot-password"
          className="auth-link forgot"
          style={{ width: 290, margin: "0 auto 0.2rem auto", fontSize: 15 }}
        >
          Forgot Password?
        </Link>
        <div style={{ marginTop: 10, fontSize: 15, textAlign: "center" }}>
          Don't have an account?
          <Link to="/register" className="auth-link" style={{ marginLeft: 4 }}>
            Register
          </Link>
        </div>
      </AuthCard>
    </CenteredPage>
  );
};

const RegisterSuccess = () => (
  <CenteredPage>
    <div className="bg-white rounded-xl shadow p-8 max-w-md w-full flex flex-col items-center">
      <div className="text-green-500 text-5xl mb-4">✔️</div>
      <h2 className="text-2xl font-bold mb-2 text-slate-800">
        Registration Successful!
      </h2>
      <div className="text-slate-700 mb-4 text-center">
        Please check your email to verify your account before logging in.
      </div>
      <Link to="/login" className="mt-2 text-blue-600 underline">
        Go to Login
      </Link>
    </div>
  </CenteredPage>
);

const emrOptions = [
  "Epic",
  "Cerner",
  "Athena",
  "eClinicalWorks",
  "Meditech",
  "Allscripts",
  "NextGen",
  "Other",
];

const Register = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    full_name: "",
    mobile: "",
    email: "",
    password: "",
    confirm_password: "",
    company_name: "",
    company_industry: "",
    emr: [],
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [emrTypes, setEmrTypes] = useState([]);
  const [loadingEmrTypes, setLoadingEmrTypes] = useState(false);
  const navigate = useNavigate();

  const labelStyle = {
    display: "block",
    width: "100%",
    marginBottom: 5,
    color: "#2d3a4a",
    fontWeight: 500,
    fontSize: 15,
  };
  const inputStyle = {
    width: "100%",
    padding: "0.85rem 1rem",
    marginBottom: 8,
    border: "1px solid #dbeafe",
    borderRadius: 10,
    fontSize: 16,
    background: "#f7fafd",
    transition: "border 0.2s",
    boxSizing: "border-box",
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Initialize EMR Types - Use hardcoded options during registration since user isn't authenticated yet
  useEffect(() => {
    // During registration, user isn't logged in yet, so we can't fetch from API
    // Use hardcoded options instead
    setEmrTypes(emrOptions.map((name) => ({ name, id: name })));
    setLoadingEmrTypes(false);
  }, []);

  const handleNext = () => {
    const errs = {};
    if (!form.full_name) errs.full_name = "Full name is required.";
    if (!form.mobile) errs.mobile = "Mobile is required.";
    if (!form.email) errs.email = "Email is required.";
    if (!form.password) {
      errs.password = "Password is required.";
    } else {
      // Strong password validation
      const passwordErrors = [];
      if (form.password.length < 8) {
        passwordErrors.push("at least 8 characters");
      }
      if (!/[A-Z]/.test(form.password)) {
        passwordErrors.push("one uppercase letter");
      }
      if (!/[a-z]/.test(form.password)) {
        passwordErrors.push("one lowercase letter");
      }
      if (!/[0-9]/.test(form.password)) {
        passwordErrors.push("one number");
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(form.password)) {
        passwordErrors.push("one special character (!@#$%^&*(),.?\":{}|<>)");
      }
      
      if (passwordErrors.length > 0) {
        errs.password = `Password must contain ${passwordErrors.join(", ")}.`;
      }
    }
    if (!form.confirm_password)
      errs.confirm_password = "Confirm password is required.";
    if (
      form.password &&
      form.confirm_password &&
      form.password !== form.confirm_password
    )
      errs.confirm_password = "Passwords do not match.";
    setFieldErrors(errs);
    if (Object.keys(errs).length === 0) setStep(2);
  };

  const validateStep2 = () => {
    const errs = {};
    if (!form.company_name) errs.company_name = "Company name is required.";
    if (!form.company_industry) errs.company_industry = "Industry is required.";
    if (!form.emr || form.emr.length === 0)
      errs.emr = "At least one EMR type is required.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (form.password !== form.confirm_password) {
      setError("Passwords do not match");
      return;
    }
    if (!validateStep2()) {
      setError("Please fill in all required fields.");
      return;
    }
    try {
      const userRes = await fetch(`${environment.apiUrl}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          full_name: form.full_name,
          mobile: form.mobile,
          company_name: form.company_name,
          industry: form.company_industry,
          emr: Array.isArray(form.emr) ? form.emr : [],
        }),
      });
      if (userRes.ok) {
        setSuccess("");
        navigate("/register-success");
        return;
      } else {
        let msg = "Registration failed.";
        try {
          const data = await userRes.json();
          if (data && data.detail) {
            msg =
              typeof data.detail === "string"
                ? data.detail
                : JSON.stringify(data.detail);
          }
        } catch {}
        setError(msg);
      }
    } catch (err) {
      setError("Network error");
    }
  };

  const industryOptions = [
    "Technology",
    "Healthcare",
    "Finance",
    "Education",
    "Retail",
    "Energy",
    "Nonprofit",
    "Other",
  ];

  return (
    <CenteredPage>
      <AuthCard title="Register">
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {step === 1 && (
            <>
              <div
                style={{
                  width: "100%",
                  fontWeight: 700,
                  fontSize: 20,
                  color: "#2d3a4a",
                  marginBottom: 16,
                  textAlign: "left",
                }}
              >
                Personal Info
              </div>
              <label htmlFor="register-fullname" style={labelStyle}>
                Full Name
              </label>
              <input
                id="register-fullname"
                name="full_name"
                type="text"
                placeholder="Enter your full name"
                value={form.full_name}
                onChange={handleChange}
                required
                style={inputStyle}
              />
              {fieldErrors.full_name && (
                <div className="text-red-500 text-xs mb-2 w-full text-left">
                  {fieldErrors.full_name}
                </div>
              )}
              <label htmlFor="register-mobile" style={labelStyle}>
                Mobile
              </label>
              <input
                id="register-mobile"
                name="mobile"
                type="text"
                placeholder="Enter your mobile number"
                value={form.mobile}
                onChange={handleChange}
                required
                style={inputStyle}
              />
              {fieldErrors.mobile && (
                <div className="text-red-500 text-xs mb-2 w-full text-left">
                  {fieldErrors.mobile}
                </div>
              )}
              <label htmlFor="register-email" style={labelStyle}>
                Email
              </label>
              <input
                id="register-email"
                name="email"
                type="email"
                placeholder="Enter your email"
                value={form.email}
                onChange={handleChange}
                required
                style={inputStyle}
              />
              {fieldErrors.email && (
                <div className="text-red-500 text-xs mb-2 w-full text-left">
                  {fieldErrors.email}
                </div>
              )}
              <label htmlFor="register-password" style={labelStyle}>
                Password
              </label>
              <input
                id="register-password"
                name="password"
                type="password"
                placeholder="Enter your password"
                value={form.password}
                onChange={handleChange}
                required
                style={inputStyle}
              />
              {fieldErrors.password && (
                <div className="text-red-500 text-xs mb-2 w-full text-left">
                  Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number, one special character (!@#$%^&*(),.?":{}|&lt;&gt;).
                </div>
              )}
              <label htmlFor="register-confirm-password" style={labelStyle}>
                Confirm Password
              </label>
              <input
                id="register-confirm-password"
                name="confirm_password"
                type="password"
                placeholder="Confirm your password"
                value={form.confirm_password}
                onChange={handleChange}
                required
                style={inputStyle}
              />
              {fieldErrors.confirm_password && (
                <div className="text-red-500 text-xs mb-2 w-full text-left">
                  {fieldErrors.confirm_password}
                </div>
              )}
              {form.password &&
                form.confirm_password &&
                form.password !== form.confirm_password && (
                  <div className="text-red-500 text-sm mb-2 w-full text-left">
                    Passwords do not match
                  </div>
                )}
              <button
                type="button"
                className="w-full mt-4 mb-2 py-2 bg-blue-500 text-white rounded-lg font-semibold text-lg hover:bg-blue-600 transition disabled:bg-gray-300 disabled:text-gray-500"
                onClick={handleNext}
              >
                Next
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <div
                style={{
                  width: "100%",
                  fontWeight: 700,
                  fontSize: 20,
                  color: "#2d3a4a",
                  marginBottom: 16,
                  textAlign: "left",
                }}
              >
                Company Info
              </div>
              <label htmlFor="register-company-name" style={labelStyle}>
                Company Name
              </label>
              <input
                id="register-company-name"
                name="company_name"
                type="text"
                placeholder="Enter company name"
                value={form.company_name}
                onChange={handleChange}
                required
                style={inputStyle}
              />
              {fieldErrors.company_name && (
                <div className="text-red-500 text-xs mb-2 w-full text-left">
                  {fieldErrors.company_name}
                </div>
              )}
              <label htmlFor="register-company-industry" style={labelStyle}>
                Industry
              </label>
              <select
                id="register-company-industry"
                name="company_industry"
                value={form.company_industry}
                onChange={handleChange}
                required
                style={inputStyle}
              >
                <option value="">Select industry</option>
                {industryOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <label htmlFor="register-company-emr" style={labelStyle}>
                EMR Type
              </label>
              <div style={{ width: "100%", marginBottom: 8 }}>
                <MultiSelectDropdown
                  options={emrTypes}
                  selectedValues={form.emr || []}
                  onChange={(values) => setForm({ ...form, emr: values })}
                  placeholder={
                    loadingEmrTypes
                      ? "Loading EMR types..."
                      : "Select EMR Type(s)"
                  }
                  displayKey="name"
                  valueKey="name"
                  disabled={loadingEmrTypes}
                  className="w-full"
                  emptyMessage="No EMR types available"
                />
              </div>
              {fieldErrors.emr && (
                <div className="text-red-500 text-xs mb-2 w-full text-left">
                  {fieldErrors.emr}
                </div>
              )}
              <div className="flex w-full justify-between mt-2 mb-2">
                <button
                  type="button"
                  className="text-blue-600 underline text-base font-semibold hover:text-blue-800 focus:outline-none"
                  onClick={() => setStep(1)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  Previous
                </button>
                <button
                  type="submit"
                  className="py-2 px-6 bg-blue-500 text-white rounded-lg font-semibold text-lg hover:bg-blue-600 transition"
                >
                  Register
                </button>
              </div>
            </>
          )}
          {error && <div className="auth-error">{error}</div>}
          {success && <div className="auth-success">{success}</div>}
        </form>
        <div style={{ width: "100%", textAlign: "center", marginTop: 16 }}>
          Already have an account?{" "}
          <Link to="/login" className="auth-link">
            Login
          </Link>
        </div>
      </AuthCard>
    </CenteredPage>
  );
};

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      const res = await fetch(`${environment.apiUrl}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setSuccess("OTP sent to your email.");
      } else {
        const data = await res.json();
        setError(data.detail ? JSON.stringify(data.detail) : "Request failed");
      }
    } catch (err) {
      setError("Network error");
    }
  };
  return (
    <CenteredPage>
      <AuthCard title={success ? "" : "Forgot Password"}>
        {success ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "220px",
            }}
          >
            <SuccessCheck />
            <div className="auth-success">{success}</div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <label
              htmlFor="forgot-email"
              style={{
                display: "block",
                width: "100%",
                marginBottom: 4,
                color: "#2d3a4a",
                fontWeight: 500,
                fontSize: 15,
              }}
            >
              Email
            </label>
            <input
              id="forgot-email"
              name="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "0.8rem 1rem",
                marginBottom: 18,
                border: "1px solid #dbeafe",
                borderRadius: 10,
                fontSize: 15,
                background: "#f7fafd",
                transition: "border 0.2s",
                boxSizing: "border-box",
              }}
            />
            <button
              type="submit"
              style={{
                width: "100%",
                padding: "0.85rem 0",
                background: "#4f8cff",
                color: "#fff",
                border: "none",
                borderRadius: 10,
                fontSize: 16,
                fontWeight: 500,
                cursor: "pointer",
                marginBottom: 14,
                transition: "background 0.2s",
              }}
            >
              Send Reset Link
            </button>
            {error && <div className="auth-error">{error}</div>}
          </form>
        )}
        {!success && (
          <div style={{ marginTop: 10, fontSize: 15, textAlign: "center" }}>
            Remembered your password?
            <Link to="/login" className="auth-link" style={{ marginLeft: 4 }}>
              Login
            </Link>
          </div>
        )}
      </AuthCard>
    </CenteredPage>
  );
};

const SuccessCheck = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      margin: "1.2rem 0 0.7rem 0",
    }}
  >
    <svg
      width="100"
      height="100"
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="50" cy="50" r="50" fill="#22c55e" />
      <path
        d="M32 54L48 70L76 38"
        stroke="#fff"
        strokeWidth="7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const VerifyOTP = () => {
  const location = useLocation();
  const [form, setForm] = useState({
    email: (location.state && location.state.email) || "",
    otp_code: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const navigate = useNavigate();
  const auth = useAuth();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      const deviceId = getOrCreateDeviceId();
      const res = await fetch(`${environment.apiUrl}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, deviceId }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.access_token) {
          setToken(data.access_token);
        }
        const meRes = await fetch(`${environment.apiUrl}/me`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });
        if (meRes.ok) {
          const userData = await meRes.json();
          auth.setUser(userData);
        }
        setSuccess("OTP verified! You can now proceed.");
        setTimeout(() => navigate("/dashboard"), 1200);
      } else {
        const data = await res.json();
        setError(
          data.detail ? JSON.stringify(data.detail) : "Verification failed"
        );
      }
    } catch (err) {
      setError("Network error");
    }
  };

  const handleResend = async () => {
    setResendMsg("");
    setError("");
    try {
      const res = await fetch(`${environment.apiUrl}/auth/resend-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email }),
      });
      if (res.ok) {
        setResendMsg("OTP sent! Check your email.");
      } else {
        const data = await res.json();
        setResendMsg(
          data.detail ? JSON.stringify(data.detail) : "Failed to resend OTP"
        );
      }
    } catch (err) {
      setResendMsg("Network error");
    }
  };

  return (
    <CenteredPage>
      <AuthCard title={success ? "" : "Verify OTP"}>
        {success ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "220px",
            }}
          >
            <SuccessCheck />
            <div className="auth-success">{success}</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-card-form">
            <input type="hidden" name="email" value={form.email} />
            <input
              className="auth-input"
              name="otp_code"
              type="text"
              placeholder="OTP Code"
              value={form.otp_code}
              onChange={handleChange}
              required
            />
            <button className="auth-btn" type="submit">
              Verify
            </button>
            {error && <div className="auth-error">{error}</div>}
            <div className="mt-4 text-center text-sm">
              Didn&apos;t receive code?{" "}
              <button
                type="button"
                className="text-blue-600 underline hover:text-blue-800"
                onClick={handleResend}
              >
                Resend
              </button>
            </div>
            {resendMsg && (
              <div className="text-green-600 text-sm mt-2">{resendMsg}</div>
            )}
          </form>
        )}
      </AuthCard>
    </CenteredPage>
  );
};

const ResetPassword = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const token = query.get("token") || "";
  const [form, setForm] = useState({
    new_password: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      const res = await fetch(`${environment.apiUrl}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          token,
          new_password: form.new_password,
        }),
      });
      if (res.ok) {
        setSuccess("Password reset successful!");
      } else {
        const data = await res.json();
        setError(data.detail ? JSON.stringify(data.detail) : "Reset failed");
      }
    } catch (err) {
      setError("Network error");
    }
  };
  return (
    <CenteredPage>
      <AuthCard title={success ? "" : "Reset Password"}>
        {success ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "220px",
            }}
          >
            <SuccessCheck />
            <div className="auth-success">
              {success} You can now{" "}
              <Link
                to="/login"
                className="text-blue-600 underline hover:text-blue-800"
              >
                login
              </Link>
              .
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-card-form">
            <input type="hidden" name="token" value={token} />
            <input
              className="auth-input"
              name="new_password"
              type="password"
              placeholder="New Password"
              value={form.new_password}
              onChange={handleChange}
              required
            />
            <button className="auth-btn" type="submit">
              Reset Password
            </button>
            {error && <div className="auth-error">{error}</div>}
          </form>
        )}
      </AuthCard>
    </CenteredPage>
  );
};

// Helper to set and get token
function setToken(token) {
  localStorage.setItem("token", token);
}
export function getToken() {
  return localStorage.getItem("token");
}

// Auth context for global state
const AuthContext = React.createContext();

function AuthProvider({ children }) {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    fetch(`${environment.apiUrl}/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        setUser(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return React.useContext(AuthContext);
}

// Protected Route
function PrivateRoute({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && user.role_id !== 3) return <Navigate to="/Clients" />;
  return children;
}

// Users page (admin only)
const Users = () => {
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [search, setSearch] = React.useState("");
  const navigate = useNavigate();
  React.useEffect(() => {
    fetch(`${environment.apiUrl}/users`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        console.log("Users API response:", data);
        console.log("First user data:", data[0]);
        setUsers(data);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load users");
        setLoading(false);
      });
  }, []);
  // Filter users by search
  const filteredUsers = users.filter((u) => {
    const searchTerm = search.toLowerCase();
    return (
      (u.full_name || "").toLowerCase().includes(searchTerm) ||
      (u.email || "").toLowerCase().includes(searchTerm) ||
      (u.role_name || u.role_id || "")
        .toString()
        .toLowerCase()
        .includes(searchTerm) ||
      (u.company_name || u.company_id || "")
        .toString()
        .toLowerCase()
        .includes(searchTerm) ||
      (u.user_type || "").toLowerCase().includes(searchTerm) ||
      (u.is_active ? "active" : "inactive").includes(searchTerm) ||
      (u.created_at || "").toLowerCase().includes(searchTerm)
    );
  });
  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-slate-800">Users</h2>
        <div className="relative">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-80 border border-slate-300 rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white shadow-sm"
          />
          <span className="absolute left-3 top-2.5 text-slate-400">🔍</span>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl shadow-lg p-6 border border-slate-200">
        {loading ? (
          <div>Loading...</div>
        ) : error ? (
          <div className="text-red-500">{error}</div>
        ) : (
          <div className="overflow-x-auto rounded-xl">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 text-slate-700 border-b border-slate-200">
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Full Name
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Role
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Company
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Active
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Created At
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u, idx) => (
                  <tr
                    key={u.id}
                    className={`border-b border-slate-100 transition-colors ${
                      idx % 2 === 0 ? "bg-white" : "bg-slate-50"
                    } hover:bg-blue-50 cursor-pointer`}
                    onClick={() => navigate(`/user/${u.id}`)}
                  >
                    <td className="px-6 py-4 font-medium text-slate-900 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                          {u.full_name ? u.full_name[0].toUpperCase() : "U"}
                        </div>
                        {u.full_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-700 whitespace-nowrap">
                      {u.email}
                    </td>
                    <td className="px-6 py-4 text-slate-700 whitespace-nowrap">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {u.role_name || u.role_id}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-700 whitespace-nowrap">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        {u.company_name || u.company_id}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          u.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {u.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-500 whitespace-nowrap">
                      {u.created_at
                        ? new Date(u.created_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Companies page (admin only)
const Companies = () => {
  const [companies, setCompanies] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [search, setSearch] = React.useState("");
  const navigate = useNavigate();
  React.useEffect(() => {
    fetch(`${environment.apiUrl}/companies`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        console.log("Companies API response:", data);
        console.log("First company data:", data[0]);
        setCompanies(data);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load companies");
        setLoading(false);
      });
  }, []);
  // Filter companies by search
  const filteredCompanies = companies.filter((c) => {
    const searchTerm = search.toLowerCase();
    return (
      (c.name || "").toLowerCase().includes(searchTerm) ||
      (c.industry || "").toLowerCase().includes(searchTerm) ||
      (c.address || "").toLowerCase().includes(searchTerm) ||
      (c.created_at || "").toLowerCase().includes(searchTerm)
    );
  });
  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-slate-800">Companies</h2>
        <div className="relative">
          <input
            type="text"
            placeholder="Search by name, industry, or address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-80 border border-slate-300 rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white shadow-sm"
          />
          <span className="absolute left-3 top-2.5 text-slate-400">🔍</span>
        </div>
      </div>

      {/* Companies Table */}
      <div className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 rounded-xl shadow-lg p-6 border border-slate-200">
        {loading ? (
          <div>Loading...</div>
        ) : error ? (
          <div className="text-red-500">{error}</div>
        ) : (
          <div className="overflow-x-auto rounded-xl">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 text-slate-700 border-b border-slate-200">
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Industry
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Address
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Created At
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Active
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredCompanies.map((c, idx) => (
                  <tr
                    key={c.id}
                    className={`border-b border-slate-100 transition-colors ${
                      idx % 2 === 0 ? "bg-white" : "bg-slate-50"
                    } hover:bg-indigo-50 cursor-pointer`}
                    onClick={() => navigate(`/companies/${c.id}`)}
                  >
                    <td className="px-6 py-4 font-medium text-slate-900 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                          {c.name ? c.name[0].toUpperCase() : "C"}
                        </div>
                        {c.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-700 whitespace-nowrap">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {c.industry || ""}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-700 whitespace-nowrap">
                      {c.address || ""}
                    </td>
                    <td className="px-6 py-4 text-slate-500 whitespace-nowrap">
                      {c.created_at
                        ? new Date(c.created_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : ""}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          c.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {c.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Modified NavBar
function NavBar() {
  const { user } = useAuth();
  return (
    <nav
      style={{
        width: "100vw",
        minWidth: 370,
        height: 60,
        borderBottom: "1px solid #ccc",
        background: "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        padding: "0 2.5rem",
      }}
    >
      {user && user.role_id === 1 && (
        <Link
          to="/users"
          style={{ marginRight: 24, fontWeight: 500, fontSize: 15 }}
        >
          Users
        </Link>
      )}
    </nav>
  );
}

const SIDEBAR_WIDTH = 220;

const Dashboard = () => {
  const [stats, setStats] = React.useState({
    totalClients: 0,
    totalSessions: 0,
    totalUsers: 0,
    recentSessions: [],
    sessionTypes: {},
    isLoading: true,
  });

  React.useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = getToken();
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch clients count
        const clientsResponse = await fetch(
          `${environment.apiUrl}/api/Clients`,
          { headers }
        );
        const clients = clientsResponse.ok ? await clientsResponse.json() : [];

        // Fetch sessions count and data
        const sessionsResponse = await fetch(`${environment.apiUrl}/sessions`, {
          headers,
        });
        const sessions = sessionsResponse.ok
          ? await sessionsResponse.json()
          : [];

        // Fetch users count
        const usersResponse = await fetch(`${environment.apiUrl}/users`, {
          headers,
        });
        const users = usersResponse.ok ? await usersResponse.json() : [];

        // Calculate session types
        const sessionTypes = sessions.reduce((acc, session) => {
          const type = session.emr_name || "Unknown";
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {});

        // Get recent sessions (last 5)
        const recentSessions = sessions
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5);

        setStats({
          totalClients: clients.length,
          totalSessions: sessions.length,
          totalUsers: users.length,
          recentSessions,
          sessionTypes,
          isLoading: false,
        });
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setStats((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchDashboardData();
  }, []);

  const StatCard = ({ title, value, icon: Icon, color, change }) => (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-600">{title}</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{value}</p>
          {change && (
            <p
              className={`text-sm font-medium mt-1 ${
                change > 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              {change > 0 ? "+" : ""}
              {change}% from last month
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-8 h-8 text-white" />
        </div>
      </div>
    </div>
  );

  const RecentSessionCard = ({ session }) => (
    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
          <CalendarIcon className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <p className="font-medium text-slate-900">
            {session.client_id_name || "Unknown Client"}
          </p>
          <p className="text-sm text-slate-500">
            {session.emr_name || "Unknown Type"}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm font-medium text-slate-900">
          {new Date(session.created_at).toLocaleDateString()}
        </p>
        <p className="text-xs text-slate-500">
          {new Date(session.created_at).toLocaleTimeString()}
        </p>
      </div>
    </div>
  );

  const SessionTypeCard = ({ type, count, total }) => {
    const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium text-slate-900">{type}</h3>
          <span className="text-sm font-medium text-slate-600">{count}</span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          {percentage}% of total sessions
        </p>
      </div>
    );
  };

  if (stats.isLoading) {
    return (
      <div className="w-full flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-8 text-white">
        <h1 className="text-3xl font-bold mb-2">Welcome to Noted</h1>
        <p className="text-blue-100 text-lg">
          Your comprehensive session management dashboard
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Clients"
          value={stats.totalClients}
          icon={UsersIcon}
          color="bg-blue-500"
          change={12}
        />
        <StatCard
          title="Total Sessions"
          value={stats.totalSessions}
          icon={ClipboardIcon}
          color="bg-green-500"
          change={8}
        />
        <StatCard
          title="Active Users"
          value={stats.totalUsers}
          icon={UserIcon}
          color="bg-purple-500"
          change={5}
        />
        <StatCard
          title="Session Types"
          value={Object.keys(stats.sessionTypes).length}
          icon={DocumentTextIcon}
          color="bg-orange-500"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Sessions */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                Recent Sessions
              </h2>
              <Link
                to="/sessions"
                className="text-blue-600 hover:text-blue-700 font-medium text-sm"
              >
                View all →
              </Link>
            </div>
            <div className="space-y-3">
              {stats.recentSessions.length > 0 ? (
                stats.recentSessions.map((session, index) => (
                  <RecentSessionCard
                    key={session.id || index}
                    session={session}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No sessions found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Session Types Breakdown */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-6">
              Session Types
            </h2>
            <div className="space-y-4">
              {Object.entries(stats.sessionTypes).length > 0 ? (
                Object.entries(stats.sessionTypes).map(([type, count]) => (
                  <SessionTypeCard
                    key={type}
                    type={type}
                    count={count}
                    total={stats.totalSessions}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <DocumentTextIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No session types found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/sessions"
            className="flex items-center p-4 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors"
          >
            <ClipboardIcon className="w-6 h-6 text-blue-600 mr-3" />
            <div>
              <p className="font-medium text-blue-900">Create Session</p>
              <p className="text-sm text-blue-700">Start a new session</p>
            </div>
          </Link>
          <Link
            to="/clients"
            className="flex items-center p-4 bg-green-50 rounded-lg border border-green-200 hover:bg-green-100 transition-colors"
          >
            <UsersIcon className="w-6 h-6 text-green-600 mr-3" />
            <div>
              <p className="font-medium text-green-900">Manage Clients</p>
              <p className="text-sm text-green-700">View and edit clients</p>
            </div>
          </Link>
          <Link
            to="/emr-types"
            className="flex items-center p-4 bg-purple-50 rounded-lg border border-purple-200 hover:bg-purple-100 transition-colors"
          >
            <DocumentTextIcon className="w-6 h-6 text-purple-600 mr-3" />
            <div>
              <p className="font-medium text-purple-900">EMR Types</p>
              <p className="text-sm text-purple-700">Configure session types</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Add Profile page
const Profile = () => {
  const { userId } = useParams();
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [editPersonal, setEditPersonal] = React.useState(false);
  const [editEMR, setEditEMR] = React.useState(false);
  const [editCompany, setEditCompany] = React.useState(false);
  const [personalForm, setPersonalForm] = React.useState(null);
  const [companyForm, setCompanyForm] = React.useState({
    name: "",
    company_industry: "",
    emr: [],
    address: "",
    is_active: true,
  });
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [resettingPw, setResettingPw] = React.useState(false);
  const [resetPwMsg, setResetPwMsg] = React.useState("");
  const [personalError, setPersonalError] = React.useState("");
  const [companyError, setCompanyError] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");

  // New fields state
  const [emrTypes, setEmrTypes] = React.useState([]);
  const [documentationMethods, setDocumentationMethods] = React.useState([]);
  const [copingSkills, setCopingSkills] = React.useState([]);
  const [clinicalSpecialties, setClinicalSpecialties] = React.useState([]);
  const [loadingOptions, setLoadingOptions] = React.useState(false);

  const navigate = useNavigate();
  const auth = useAuth();

  // Load dropdown options
  React.useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const [
          emrTypesData,
          docMethodsData,
          copingSkillsData,
          specialtiesData,
        ] = await Promise.all([
          api.getEMRTypes(),
          api.getDocumentationMethods(),
          api.getCopingSkills(),
          api.getClinicalSpecialties(),
        ]);

        setEmrTypes(emrTypesData);
        setDocumentationMethods(docMethodsData);
        setCopingSkills(copingSkillsData);
        setClinicalSpecialties(specialtiesData);
      } catch (error) {
        console.error("Failed to load dropdown options:", error);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, []);

  React.useEffect(() => {
    const url = userId
      ? `${environment.apiUrl}/users/${userId}`
      : `${environment.apiUrl}/me`;
    fetch(url, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        setUser(data);
        setPersonalForm({
          full_name: data.full_name || "",
          email: data.email || "",
          mobile_phone: data.mobile_phone || "",
          is_active: data.is_active,
          user_type: data.user_type || "",
          session_instructions: data.session_instructions || "",
          emr_types: data.emr_types || [],
          documentation_methods: data.documentation_methods || [],
          emr_type_documentation_pairs: data.emr_type_documentation_pairs || [],
          coping_skills: data.coping_skills || [],
          clinical_specialties: data.clinical_specialties || [],
          type_writing: data.type_writing || [],
        });
        // Handle backward compatibility: convert string EMR to array if needed
        const emrValue = data.company?.emr || data.emr || [];
        const emrArray = Array.isArray(emrValue)
          ? emrValue
          : emrValue
          ? [emrValue]
          : [];

        setCompanyForm({
          name: data.company?.name || data.company_name || "",
          company_industry: data.company?.industry || data.industry || "",
          emr: emrArray,
          address: data.company?.address || data.address || "",
          is_active:
            typeof data.company?.is_active === "boolean"
              ? data.company.is_active
              : true,
        });
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load profile");
        setLoading(false);
      });
  }, [userId]);

  const labelClass = "text-gray-500 text-sm font-medium";
  const valueClass = "text-slate-800 font-semibold";
  const naClass = "text-gray-400 font-semibold";

  const handleSavePersonal = async () => {
    setPersonalError("");
    setSuccessMessage("");
    try {
      // Use the original email if it was not changed
      const emailToSend =
        personalForm.email &&
        personalForm.email.trim().toLowerCase() !==
          user.email.trim().toLowerCase()
          ? personalForm.email.trim()
          : user.email;
      const payload = {
        full_name: personalForm.full_name,
        email: emailToSend,
        is_active: personalForm.is_active,
        mobile_phone: personalForm.mobile_phone
          ? personalForm.mobile_phone
          : null,
        user_type: personalForm.user_type ? personalForm.user_type : null,
        session_instructions: personalForm.session_instructions || null,
        emr_types: personalForm.emr_types || [],
        documentation_methods: personalForm.documentation_methods || [],
        emr_type_documentation_pairs:
          personalForm.emr_type_documentation_pairs || [],
        coping_skills: personalForm.coping_skills || [],
        clinical_specialties: personalForm.clinical_specialties || [],
        type_writing: personalForm.type_writing || [],
      };
      const url = userId
        ? `${environment.apiUrl}/users/${userId}`
        : `${environment.apiUrl}/me`;
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const updated = await res.json();
        setUser((u) => ({ ...u, ...updated }));
        setEditPersonal(false);
        setEditEMR(false);
        setEditCompany(false);
        setSuccessMessage("Profile updated successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        let msg = "Failed to update personal info.";
        try {
          const data = await res.json();
          if (data && data.detail) {
            msg =
              typeof data.detail === "string"
                ? data.detail
                : JSON.stringify(data.detail);
          }
        } catch {}
        setPersonalError(msg);
      }
    } catch {
      setPersonalError("Failed to update personal info.");
    }
  };

  const handleSaveCompany = async () => {
    setCompanyError("");
    setSuccessMessage("");
    try {
      const companyId = user.company?.id;
      if (!companyId) {
        setCompanyError("No company ID found.");
        return;
      }
      const res = await fetch(`${environment.apiUrl}/company/${companyId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          name: companyForm.name,
          industry: companyForm.company_industry,
          emr: Array.isArray(companyForm.emr) ? companyForm.emr : [],
          address: companyForm.address,
          is_active: companyForm.is_active,
        }),
      });
      if (res.ok) {
        const updated = await res.json();
        setUser((u) => ({ ...u, company: { ...u.company, ...updated } }));
        setEditPersonal(false);
        setEditEMR(false);
        setEditCompany(false);
        setCompanyError("");
        setSuccessMessage("Company information updated successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        let msg = "Failed to update company info.";
        try {
          const data = await res.json();
          if (data && data.detail) {
            msg =
              typeof data.detail === "string"
                ? data.detail
                : JSON.stringify(data.detail);
          }
        } catch {}
        setCompanyError(msg);
      }
    } catch {
      setCompanyError("Failed to update company info.");
    }
  };

  const handleDeleteUser = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this user? This action cannot be undone."
      )
    )
      return;
    try {
      const res = await fetch(`${environment.apiUrl}/users/${user.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) {
        navigate("/users");
      } else {
        alert("Failed to delete user.");
      }
    } catch {
      alert("Failed to delete user.");
    }
  };

  return (
    <div className="w-full flex flex-col items-start justify-center">
      <h2 className="text-3xl font-extrabold text-slate-800 mb-8 tracking-tight text-center w-full">
        Profile
      </h2>
      {/* Modal for delete confirmation */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-sm w-full flex flex-col items-center">
            <div className="text-xl font-bold mb-4 text-red-600">
              Are you sure?
            </div>
            <div className="mb-6 text-slate-700 text-center">
              Are you sure you want to delete this user? This action cannot be
              undone.
            </div>
            <div className="flex gap-4">
              <button
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-semibold"
                onClick={async () => {
                  setShowDeleteModal(false);
                  await handleDeleteUser();
                }}
              >
                Delete
              </button>
              <button
                className="px-4 py-2 bg-gray-200 text-slate-700 rounded hover:bg-gray-300 font-semibold"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="w-full max-w-6xl mx-auto bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl p-8 shadow-xl">
        {loading ? (
          <div>Loading...</div>
        ) : error ? (
          <div className="text-red-500">{error}</div>
        ) : user ? (
          <>
            {/* Success Message */}
            {successMessage && (
              <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg shadow-sm">
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  {successMessage}
                </div>
              </div>
            )}

            {/* User Header - Top Center */}
            <div className="flex flex-col items-center mb-8">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-4xl font-bold text-white shadow-lg mb-4">
                {user.full_name
                  ? user.full_name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                  : "U"}
              </div>
              <h1 className="text-3xl font-extrabold text-slate-800 mb-2">
                {user.full_name}
              </h1>
              <p className="text-slate-500 text-lg">{user.email}</p>
            </div>

            {/* Three Sections Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Personal Info Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-100 relative">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">👤</span>
                  <span className="text-lg font-bold text-slate-700 tracking-wide">
                    Personal Info
                  </span>
                  {!editPersonal && !editEMR && !editCompany && (
                    <button
                      className="text-gray-400 hover:text-blue-600 text-xl ml-auto"
                      onClick={() => {
                        setEditPersonal(true);
                        setEditEMR(false);
                        setEditCompany(false);
                        setSuccessMessage("");
                      }}
                      title="Edit Personal Info"
                    >
                      ✏️
                    </button>
                  )}
                  {auth.user?.role_id === 3 && (
                    <button
                      className="px-2.5 py-0.5 bg-red-600 text-white rounded shadow hover:bg-red-700 font-medium text-xs ml-2"
                      onClick={() => setShowDeleteModal(true)}
                    >
                      Delete User
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className={labelClass}>Mobile:</span>
                    {editPersonal ? (
                      <input
                        className="border-b border-slate-200 focus:outline-none focus:border-blue-400 px-1 py-0.5 w-32"
                        value={personalForm.mobile_phone}
                        onChange={(e) =>
                          setPersonalForm((f) => ({
                            ...f,
                            mobile_phone: e.target.value,
                          }))
                        }
                      />
                    ) : (
                      <span
                        className={user.mobile_phone ? valueClass : naClass}
                      >
                        {user.mobile_phone || ""}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={labelClass}>Company:</span>
                    <span
                      className={
                        user.company?.name || user.company_name
                          ? valueClass
                          : naClass
                      }
                    >
                      {user.company?.name || user.company_name || ""}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={labelClass}>Password:</span>
                    <span className={valueClass}>********</span>
                    <button
                      className="text-blue-600 text-xs underline hover:text-blue-800 focus:outline-none ml-2"
                      type="button"
                      disabled={resettingPw}
                      onClick={async () => {
                        setResettingPw(true);
                        setResetPwMsg("");
                        try {
                          const res = await fetch(
                            `${environment.apiUrl}/auth/forgot-password`,
                            {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              credentials: "include",
                              body: JSON.stringify({ email: user.email }),
                            }
                          );
                          if (res.ok) {
                            setResetPwMsg("Reset link sent to your email.");
                          } else {
                            const data = await res.json();
                            setResetPwMsg(
                              data.detail
                                ? JSON.stringify(data.detail)
                                : "Request failed"
                            );
                          }
                        } catch (err) {
                          setResetPwMsg("Network error");
                        } finally {
                          setResettingPw(false);
                        }
                      }}
                    >
                      Forgot password?
                    </button>
                    {resetPwMsg && (
                      <span className="block text-xs mt-1 ml-2 text-green-600">
                        {resetPwMsg}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={labelClass}>Active:</span>
                    {editPersonal && auth.user?.role_id === 3 ? (
                      <select
                        className="block w-40 px-2 py-1 mt-1 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 text-slate-700"
                        value={personalForm.is_active ? "true" : "false"}
                        onChange={(e) =>
                          setPersonalForm((f) => ({
                            ...f,
                            is_active: e.target.value === "true",
                          }))
                        }
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    ) : (
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {user.is_active ? "Active" : "Inactive"}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={labelClass}>User Type:</span>
                    {editPersonal ? (
                      <select
                        className="block w-40 px-2 py-1 mt-1 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 text-slate-700"
                        value={personalForm.user_type}
                        onChange={(e) =>
                          setPersonalForm((f) => ({
                            ...f,
                            user_type: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select user type</option>
                        {industryOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className={user.user_type ? valueClass : naClass}>
                        {user.user_type || ""}
                      </span>
                    )}
                  </div>

                  <div className="flex items-start gap-2">
                    <span className={labelClass}>Session Instructions:</span>
                    {editPersonal ? (
                      <textarea
                        className="border-b border-slate-200 focus:outline-none focus:border-blue-400 px-1 py-0.5 w-full min-h-[60px] resize-none"
                        value={personalForm.session_instructions || ""}
                        onChange={(e) =>
                          setPersonalForm((f) => ({
                            ...f,
                            session_instructions: e.target.value,
                          }))
                        }
                        placeholder="Enter any special session instructions"
                      />
                    ) : (
                      <div className="flex-1 min-w-0">
                        <span
                          className={`${
                            user.session_instructions ? valueClass : naClass
                          }`}
                          style={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                          title={user.session_instructions || ""}
                        >
                          {user.session_instructions || ""}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                {editPersonal && (
                  <>
                    <div className="flex gap-2 mt-6">
                      <button
                        className="px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                        onClick={handleSavePersonal}
                      >
                        Save
                      </button>
                      <button
                        className="px-4 py-1 bg-gray-200 text-slate-700 rounded hover:bg-gray-300"
                        onClick={() => {
                          setEditPersonal(false);
                          setEditEMR(false);
                          setEditCompany(false);
                          setPersonalForm({
                            full_name: user.full_name,
                            email: user.email,
                            mobile_phone: user.mobile_phone,
                            is_active: user.is_active,
                            user_type: user.user_type || "",
                            session_instructions:
                              user.session_instructions || "",
                            emr_types: user.emr_types || [],
                            documentation_methods:
                              user.documentation_methods || [],
                            emr_type_documentation_pairs:
                              user.emr_type_documentation_pairs || [],
                            coping_skills: user.coping_skills || [],
                            clinical_specialties:
                              user.clinical_specialties || [],
                            type_writing: user.type_writing || [],
                          });
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    {personalError && (
                      <div className="text-red-500 text-sm mt-2 w-full text-center">
                        {personalError}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* EMR Info Card - Middle */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-100 relative">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">💻</span>
                  <span className="text-lg font-bold text-slate-700 tracking-wide">
                    EMR Personalization
                  </span>
                  {!editPersonal && !editEMR && !editCompany && (
                    <button
                      className="text-gray-400 hover:text-blue-600 text-xl ml-auto"
                      onClick={() => {
                        setEditPersonal(false);
                        setEditEMR(true);
                        setEditCompany(false);
                        setSuccessMessage("");
                      }}
                      title="Edit EMR Info"
                    >
                      ✏️
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  {/* EMR Types + Documentation Methods Pairs */}
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      {editEMR ? (
                        <>
                          {(() => {
                            const companyEmrArray = companyForm.emr || [];
                            const availableEmrTypes =
                              companyEmrArray.length === 0
                                ? []
                                : emrTypes.filter((emrType) =>
                                    companyEmrArray.includes(emrType.name)
                                  );

                            if (companyEmrArray.length === 0) {
                              return (
                                <div className="text-amber-600 text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">
                                  ⚠️ No EMR types available. Please select EMR
                                  types for your company first in the Company
                                  Info section.
                                </div>
                              );
                            }

                            if (availableEmrTypes.length === 0) {
                              return (
                                <div className="text-amber-600 text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">
                                  ⚠️ No matching EMR types found. The EMR types
                                  selected for your company may not be available
                                  in the system.
                                </div>
                              );
                            }

                            return (
                              <EMRTypeDocumentationPairs
                                emrTypes={availableEmrTypes}
                                documentationMethods={documentationMethods}
                                selectedPairs={
                                  personalForm.emr_type_documentation_pairs ||
                                  []
                                }
                                onChange={(pairs) =>
                                  setPersonalForm((f) => ({
                                    ...f,
                                    emr_type_documentation_pairs: pairs,
                                  }))
                                }
                                placeholder="Select EMR Type + Documentation Method pairs..."
                                disabled={loadingOptions}
                              />
                            );
                          })()}
                        </>
                      ) : (
                        <div>
                          {loadingOptions ? (
                            <span className="text-gray-500 text-sm">
                              Loading...
                            </span>
                          ) : (user.emr_type_documentation_pairs &&
                              user.emr_type_documentation_pairs.length > 0) ||
                            (user.emr_types &&
                              user.emr_types.length > 0 &&
                              user.documentation_methods &&
                              user.documentation_methods.length > 0) ? (
                            <div className="flex flex-wrap gap-2">
                              {/* Show new format pairs if available */}
                              {user.emr_type_documentation_pairs &&
                              user.emr_type_documentation_pairs.length > 0
                                ? user.emr_type_documentation_pairs.map(
                                    (pair) => (
                                      <span
                                        key={
                                          pair.id ||
                                          `${pair.emr_type_id}-${pair.documentation_method_id}`
                                        }
                                        className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800"
                                      >
                                        {pair.display_text ||
                                          `${
                                            pair.emr_type_name || pair.emr_type
                                          } + ${
                                            pair.documentation_method_name ||
                                            pair.documentation_method
                                          }`}
                                      </span>
                                    )
                                  )
                                : /* Fallback: create pairs from individual EMR types and documentation methods */
                                  (() => {
                                    const emrTypeNames = user.emr_types
                                      ? user.emr_types.map((emrType) => {
                                          return typeof emrType === "string"
                                            ? emrTypes.find(
                                                (opt) => opt.id === emrType
                                              )?.name || emrType
                                            : emrType.name;
                                        })
                                      : [];

                                    const docMethodNames =
                                      user.documentation_methods
                                        ? user.documentation_methods.map(
                                            (method) => {
                                              return typeof method === "string"
                                                ? documentationMethods.find(
                                                    (opt) => opt.id === method
                                                  )?.name || method
                                                : method.name;
                                            }
                                          )
                                        : [];

                                    // Create pairs by combining EMR types with documentation methods
                                    const pairs = [];
                                    if (
                                      emrTypeNames.length > 0 &&
                                      docMethodNames.length > 0
                                    ) {
                                      // If both exist, create all possible combinations
                                      emrTypeNames.forEach((emrType) => {
                                        docMethodNames.forEach((docMethod) => {
                                          pairs.push(
                                            `${emrType} + ${docMethod}`
                                          );
                                        });
                                      });
                                    } else if (emrTypeNames.length > 0) {
                                      // If only EMR types exist, show them as individual items
                                      emrTypeNames.forEach((emrType) => {
                                        pairs.push(emrType);
                                      });
                                    } else if (docMethodNames.length > 0) {
                                      // If only documentation methods exist, show them as individual items
                                      docMethodNames.forEach((docMethod) => {
                                        pairs.push(docMethod);
                                      });
                                    }

                                    return pairs.map((pair, index) => (
                                      <span
                                        key={index}
                                        className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800"
                                      >
                                        {pair}
                                      </span>
                                    ));
                                  })()}
                            </div>
                          ) : (
                            <span className={naClass}>None selected</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Coping Skills */}
                  <div className="flex items-start gap-2">
                    <span className={labelClass}>Coping Skills:</span>
                    <div className="flex-1">
                      {editEMR ? (
                        <MultiSelectDropdown
                          options={copingSkills}
                          selectedValues={personalForm.coping_skills || []}
                          onChange={(values) =>
                            setPersonalForm((f) => ({
                              ...f,
                              coping_skills: values,
                            }))
                          }
                          placeholder="Select coping skills..."
                          displayKey="short_description"
                          valueKey="id"
                          disabled={loadingOptions}
                        />
                      ) : (
                        <div>
                          {loadingOptions ? (
                            <span className="text-gray-500 text-sm">
                              Loading...
                            </span>
                          ) : user.coping_skills &&
                            user.coping_skills.length > 0 ? (
                            <div className="flex flex-wrap gap-1 items-center">
                              {user.coping_skills.map((skill) => {
                                const id =
                                  typeof skill === "string" ? skill : skill.id;
                                const name =
                                  typeof skill === "string"
                                    ? copingSkills.find(
                                        (opt) => opt.id === skill
                                      )?.short_description || skill
                                    : skill.short_description || skill.name;
                                const longDesc =
                                  typeof skill === "string"
                                    ? copingSkills.find(
                                        (opt) => opt.id === skill
                                      )?.long_description ||
                                      "No description available"
                                    : skill.long_description ||
                                      "No description available";

                                // Don't show raw IDs if we haven't loaded the options yet
                                if (
                                  typeof skill === "string" &&
                                  copingSkills.length === 0
                                ) {
                                  return null;
                                }

                                return (
                                  <div key={id} className="relative group">
                                    <span className="inline-flex items-center gap-2 px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                                      {name}
                                      <span className="text-gray-600 hover:text-gray-800 cursor-help text-xs bg-gray-300 rounded-full w-4 h-4 flex items-center justify-center">
                                        i
                                      </span>
                                    </span>
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-white text-black text-xs rounded-lg border border-gray-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 max-w-xs">
                                      <div className="max-w-xs break-words">
                                        {longDesc}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <span className={naClass}>None selected</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Clinical Specialties */}
                  <div className="flex items-start gap-2">
                    <span className={labelClass}>Clinical Specialties:</span>
                    <div className="flex-1">
                      {editEMR ? (
                        <MultiSelectDropdown
                          options={clinicalSpecialties}
                          selectedValues={
                            personalForm.clinical_specialties || []
                          }
                          onChange={(values) =>
                            setPersonalForm((f) => ({
                              ...f,
                              clinical_specialties: values,
                            }))
                          }
                          placeholder="Select clinical specialties..."
                          displayKey="short_description"
                          valueKey="id"
                          disabled={loadingOptions}
                        />
                      ) : (
                        <div>
                          {loadingOptions ? (
                            <span className="text-gray-500 text-sm">
                              Loading...
                            </span>
                          ) : user.clinical_specialties &&
                            user.clinical_specialties.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {user.clinical_specialties.map((specialty) => {
                                const id =
                                  typeof specialty === "string"
                                    ? specialty
                                    : specialty.id;
                                const name =
                                  typeof specialty === "string"
                                    ? clinicalSpecialties.length > 0
                                      ? clinicalSpecialties.find(
                                          (opt) => opt.id === specialty
                                        )?.short_description || specialty
                                      : specialty
                                    : specialty.short_description ||
                                      specialty.name;

                                const longDesc =
                                  typeof specialty === "string"
                                    ? clinicalSpecialties.find(
                                        (opt) => opt.id === specialty
                                      )?.long_description ||
                                      "No description available"
                                    : specialty.long_description ||
                                      "No description available";

                                // Don't show raw IDs if we haven't loaded the options yet
                                if (
                                  typeof specialty === "string" &&
                                  clinicalSpecialties.length === 0
                                ) {
                                  return null;
                                }

                                return (
                                  <div key={id} className="relative group">
                                    <span
                                      className="inline-flex items-center gap-2 px-2 py-1 rounded-md text-xs font-medium bg-rose-100 border border-rose-200"
                                      style={{
                                        color: "#be185d",
                                        backgroundColor: "#fdf2f8",
                                        borderColor: "#fce7f3",
                                      }}
                                    >
                                      {name}
                                      <span className="text-gray-600 hover:text-gray-800 cursor-help text-xs bg-gray-300 rounded-full w-4 h-4 flex items-center justify-center">
                                        i
                                      </span>
                                    </span>
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-white text-black text-xs rounded-lg border border-gray-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 max-w-xs">
                                      <div className="max-w-xs break-words">
                                        {longDesc}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <span className={naClass}>None selected</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Type Writing */}
                  <div className="flex items-start gap-2">
                    <span className={labelClass}>Type Writing:</span>
                    <div className="flex-1">
                      {editEMR ? (
                        <MultiSelectDropdown
                          options={typeWritingOptions}
                          selectedValues={personalForm.type_writing || []}
                          onChange={(values) =>
                            setPersonalForm((f) => ({
                              ...f,
                              type_writing: values,
                            }))
                          }
                          placeholder="Select type writing options..."
                          disabled={loadingOptions}
                        />
                      ) : (
                        <div>
                          {user.type_writing && user.type_writing.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {user.type_writing.map((type) => (
                                <span
                                  key={type}
                                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-indigo-100 text-indigo-800"
                                >
                                  {type}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className={naClass}>None selected</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Save/Cancel buttons for EMR Info */}
                {editEMR && (
                  <div className="flex gap-2 mt-6">
                    <button
                      className="px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      onClick={handleSavePersonal}
                    >
                      Save
                    </button>
                    <button
                      className="px-4 py-1 bg-gray-200 text-slate-700 rounded hover:bg-gray-300"
                      onClick={() => {
                        setEditPersonal(false);
                        setEditEMR(false);
                        setEditCompany(false);
                        setPersonalForm({
                          full_name: user.full_name,
                          email: user.email,
                          mobile_phone: user.mobile_phone,
                          is_active: user.is_active,
                          user_type: user.user_type || "",
                          session_instructions: user.session_instructions || "",
                          emr_types: user.emr_types || [],
                          documentation_methods:
                            user.documentation_methods || [],
                          emr_type_documentation_pairs:
                            user.emr_type_documentation_pairs || [],
                          coping_skills: user.coping_skills || [],
                          clinical_specialties: user.clinical_specialties || [],
                          type_writing: user.type_writing || [],
                        });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              {/* Company Info Card - Right */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-100 relative">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🏢</span>
                  <span className="text-lg font-bold text-slate-700 tracking-wide">
                    Company Info
                  </span>
                  {!editPersonal &&
                    !editEMR &&
                    !editCompany &&
                    auth.user.role_id === 3 && (
                      <button
                        className="text-gray-400 hover:text-blue-600 text-xl ml-auto"
                        onClick={() => {
                          setEditPersonal(false);
                          setEditEMR(false);
                          setEditCompany(true);
                          setSuccessMessage("");
                        }}
                        title="Edit Company Info"
                      >
                        ✏️
                      </button>
                    )}
                </div>
                <div className="space-y-3">
                  {editCompany ? (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🏢</span>
                        <span className={labelClass}>Name:</span>
                        <input
                          className="border-b border-slate-200 focus:outline-none focus:border-blue-400 px-1 py-0.5 w-40"
                          value={companyForm.name}
                          onChange={(e) =>
                            setCompanyForm((f) => ({
                              ...f,
                              name: e.target.value,
                            }))
                          }
                          maxLength={100}
                        />
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-lg">💻</span>
                        <span className={labelClass}>EMR:</span>
                        <div className="flex-1 min-w-[200px]">
                          <MultiSelectDropdown
                            options={emrTypes}
                            selectedValues={companyForm.emr || []}
                            onChange={(values) =>
                              setCompanyForm((f) => ({
                                ...f,
                                emr: values,
                              }))
                            }
                            placeholder={
                              loadingOptions
                                ? "Loading EMR types..."
                                : "Select EMR Type(s)"
                            }
                            displayKey="name"
                            valueKey="name"
                            disabled={loadingOptions}
                            emptyMessage="No EMR types available"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🏷️</span>
                        <span className={labelClass}>Industry:</span>
                        <select
                          className="block w-40 px-2 py-1 mt-1 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 text-slate-700"
                          value={companyForm.company_industry}
                          onChange={(e) =>
                            setCompanyForm((f) => ({
                              ...f,
                              company_industry: e.target.value,
                            }))
                          }
                        >
                          <option value="">Select Industry</option>
                          {industryOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">📍</span>
                        <span className={labelClass}>Address:</span>
                        <input
                          className="border-b border-slate-200 focus:outline-none focus:border-blue-400 px-1 py-0.5 w-32"
                          value={companyForm.address}
                          onChange={(e) =>
                            setCompanyForm((f) => ({
                              ...f,
                              address: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="flex items-center gap-2 ml-7">
                        <span className={labelClass}>Active:</span>
                        {auth.user?.role_id === 3 ? (
                          <select
                            className="block w-28 px-2 py-1 mt-1 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 text-slate-700"
                            value={companyForm.is_active ? "true" : "false"}
                            onChange={(e) =>
                              setCompanyForm((f) => ({
                                ...f,
                                is_active: e.target.value === "true",
                              }))
                            }
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        ) : null}
                      </div>
                      <div className="flex gap-2 mt-6">
                        <button
                          className="px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          onClick={handleSaveCompany}
                        >
                          Save
                        </button>
                        <button
                          className="px-4 py-1 bg-gray-200 text-slate-700 rounded hover:bg-gray-300"
                          onClick={() => {
                            setEditPersonal(false);
                            setEditEMR(false);
                            setEditCompany(false);
                            // Handle backward compatibility: convert string EMR to array if needed
                            const emrValue = user.company?.emr || "";
                            const emrArray = Array.isArray(emrValue)
                              ? emrValue
                              : emrValue
                              ? [emrValue]
                              : [];

                            setCompanyForm({
                              name: user.company?.name || "",
                              company_industry: user.company?.industry || "",
                              emr: emrArray,
                              address: user.company?.address || "",
                              is_active: user.company?.is_active || true,
                            });
                            setCompanyError("");
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                      {companyError && (
                        <div className="text-red-500 text-sm mt-2 w-full text-center">
                          {companyError}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🏢</span>
                        <span className={labelClass}>Name:</span>
                        <span
                          className={
                            user.company?.name || user.company_name
                              ? valueClass
                              : naClass
                          }
                        >
                          {user.company?.name || user.company_name || ""}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-lg">💻</span>
                        <span className={labelClass}>EMR:</span>
                        <div className="flex-1">
                          {(() => {
                            const emrValue =
                              user.company?.emr || user.emr || [];
                            const emrArray = Array.isArray(emrValue)
                              ? emrValue
                              : emrValue
                              ? [emrValue]
                              : [];

                            if (emrArray.length === 0) {
                              return (
                                <span className={naClass}>No EMR selected</span>
                              );
                            }

                            return (
                              <div className="flex flex-wrap gap-1">
                                {emrArray.map((emr, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                                  >
                                    {emr}
                                  </span>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🏷️</span>
                        <span className={labelClass}>Industry:</span>
                        <span
                          className={
                            user.company?.industry || user.industry
                              ? valueClass
                              : naClass
                          }
                        >
                          {user.company?.industry || user.industry || ""}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">📍</span>
                        <span className={labelClass}>Address:</span>
                        <span
                          className={
                            user.company?.address || user.address
                              ? valueClass
                              : naClass
                          }
                        >
                          {user.company?.address || user.address || ""}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 ml-7">
                        <span className={labelClass}>Active:</span>
                        {editCompany && user.role_id === 3 ? (
                          <select
                            className="block w-28 px-2 py-1 mt-1 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 text-slate-700"
                            value={companyForm.is_active ? "true" : "false"}
                            onChange={(e) =>
                              setCompanyForm((f) => ({
                                ...f,
                                is_active: e.target.value === "true",
                              }))
                            }
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        ) : (
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.company?.is_active
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-200 text-gray-600"
                            }`}
                          >
                            {user.company?.is_active ? "Active" : "Inactive"}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

const VerifyEmail = () => {
  const [status, setStatus] = React.useState("loading");
  const [message, setMessage] = React.useState("");
  const location = useLocation();
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setMessage("No verification token provided.");
      return;
    }
    fetch(
      `https://noteddevapi.objectif.solutions/api/v1/auth/verify-email?token=${encodeURIComponent(
        token
      )}`
    )
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok) {
          setStatus("success");
          setMessage(
            "Your email has been successfully verified! You can now log in."
          );
        } else {
          setStatus("error");
          setMessage(data.detail || "Verification failed.");
        }
      })
      .catch(() => {
        setStatus("error");
        setMessage("Verification failed.");
      });
  }, [location.search]);
  return (
    <CenteredPage>
      <div className="bg-white rounded-xl shadow p-8 max-w-md w-full flex flex-col items-center">
        <h2 className="text-2xl font-bold mb-4">Verify Email</h2>
        {status === "loading" ? (
          <div>Verifying...</div>
        ) : status === "success" ? (
          <div className="text-green-600 font-semibold mb-2">{message}</div>
        ) : (
          <div className="text-red-500 font-semibold mb-2">{message}</div>
        )}
        <Link to="/login" className="mt-4 text-blue-600 underline">
          Go to Login
        </Link>
      </div>
    </CenteredPage>
  );
};

function CompanyDetails() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    industry: "",
    emr: [],
    address: "",
    is_active: true,
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [emrTypes, setEmrTypes] = useState([]);
  const [loadingEmrTypes, setLoadingEmrTypes] = useState(false);
  const navigate = useNavigate();
  const labelClass = "text-gray-500 text-sm font-medium";
  const { user } = useAuth();

  useEffect(() => {
    setLoading(true);
    fetch(`${environment.apiUrl}/companies/${companyId}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        setCompany(data);
        // Handle backward compatibility: convert string EMR to array if needed
        const emrValue = data.emr || [];
        const emrArray = Array.isArray(emrValue)
          ? emrValue
          : emrValue
          ? [emrValue]
          : [];

        setEditForm({
          name: data.name || "",
          industry: data.industry || "",
          emr: emrArray,
          address: data.address || "",
          is_active:
            typeof data.is_active === "boolean" ? data.is_active : true,
        });
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load company details");
        setLoading(false);
      });
  }, [companyId]);

  // Load EMR Types
  useEffect(() => {
    const loadEmrTypes = async () => {
      setLoadingEmrTypes(true);
      try {
        const data = await api.getEMRTypes();
        setEmrTypes(data);
      } catch (error) {
        console.error("Failed to load EMR types:", error);
      } finally {
        setLoadingEmrTypes(false);
      }
    };
    loadEmrTypes();
  }, []);

  if (loading) return <div className="text-center py-8">Loading...</div>;
  if (error)
    return <div className="text-center text-red-500 py-8">{error}</div>;
  if (!company) return null;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate("/companies")}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
        >
          ← Back to Companies
        </button>
      </div>

      {/* Company Information Card */}
      <div className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 rounded-xl shadow-lg p-6 mb-6 border border-slate-200 relative">
        <button
          className="absolute top-6 right-6 text-gray-400 hover:text-blue-600 text-xl"
          title="Edit Company Info"
          onClick={() => setEditMode(true)}
          style={{ display: editMode ? "none" : "block" }}
        >
          ✏️
        </button>
        <div className="flex items-center gap-4 mb-6">
          {/* Company Avatar */}
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-extrabold text-2xl shadow-lg">
            {company.name ? company.name[0].toUpperCase() : "C"}
          </div>

          {/* Company Name */}
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              {editMode ? (
                <input
                  className="text-3xl font-bold text-slate-900 bg-transparent border-b border-slate-300 focus:outline-none focus:border-indigo-400"
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, name: e.target.value }))
                  }
                  maxLength={100}
                />
              ) : (
                company.name
              )}
            </h1>
            <p className="text-slate-500 text-base">Company Information</p>
          </div>
        </div>
        {editMode ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-24 gap-y-8 mb-4">
            <div className="flex items-center gap-4">
              <span className="text-lg">🏭</span>
              <span className={labelClass}>Industry:</span>
              <span className="ml-1 text-slate-800">
                <select
                  className="block w-36 px-2 py-1 mt-1 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 text-slate-700"
                  value={editForm.industry}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, industry: e.target.value }))
                  }
                >
                  <option value="">Select Industry</option>
                  {industryOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </span>
            </div>
            <div className="flex items-start gap-4 ml-24">
              <span className="text-lg">🏥</span>
              <span className={labelClass}>EMR:</span>
              <div className="flex-1 min-w-[200px]">
                <MultiSelectDropdown
                  options={emrTypes}
                  selectedValues={editForm.emr || []}
                  onChange={(values) =>
                    setEditForm((f) => ({ ...f, emr: values }))
                  }
                  placeholder={
                    loadingEmrTypes
                      ? "Loading EMR types..."
                      : "Select EMR Type(s)"
                  }
                  displayKey="name"
                  valueKey="name"
                  disabled={loadingEmrTypes}
                  emptyMessage="No EMR types available"
                />
              </div>
            </div>
            <div></div>
            <div className="flex items-center gap-4">
              <span className="text-lg">📍</span>
              <span className={labelClass}>Address:</span>
              <span className="ml-1 text-slate-800">
                <input
                  className="border-b border-slate-200 focus:outline-none focus:border-blue-400 px-1 py-0.5 w-32"
                  value={editForm.address}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, address: e.target.value }))
                  }
                />
              </span>
            </div>
            <div className="flex items-center gap-4 ml-24">
              <span className={labelClass}>Active:</span>
              {user.role_id === 3 ? (
                <select
                  className="block w-28 px-2 py-1 mt-1 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 text-slate-700"
                  value={editForm.is_active ? "true" : "false"}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      is_active: e.target.value === "true",
                    }))
                  }
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              ) : (
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    company.is_active
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {company.is_active ? "Active" : "Inactive"}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Company Details */}
            <div className="bg-white/70 backdrop-blur-sm rounded-lg p-5 border border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <BuildingOfficeIcon className="w-6 h-6 text-black" />
                Company Details
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <BuildingOfficeIcon className="w-6 h-6 text-black" />
                  <div>
                    <p className="text-sm font-bold text-slate-900">Industry</p>
                    <p className="text-slate-500 font-normal">
                      {company.industry || ""}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <BuildingOffice2Icon className="w-6 h-6 text-black" />
                  <div>
                    <p className="text-sm font-bold text-slate-900">EMR</p>
                    {(() => {
                      const emrValue = company.emr || [];
                      const emrArray = Array.isArray(emrValue)
                        ? emrValue
                        : emrValue
                        ? [emrValue]
                        : [];

                      if (emrArray.length === 0) {
                        return (
                          <p className="text-slate-400 font-normal">
                            No EMR selected
                          </p>
                        );
                      }

                      return (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {emrArray.map((emr, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {emr}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <MapPinIcon className="w-6 h-6 text-black" />
                  <div>
                    <p className="text-sm font-bold text-slate-900">Address</p>
                    <p className="text-slate-500 font-normal">
                      {company.address || ""}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Information */}
            <div className="bg-white/70 backdrop-blur-sm rounded-lg p-5 border border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <ChartBarIcon className="w-6 h-6 text-black" />
                Status Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-black" />
                  <div>
                    <p className="text-sm font-bold text-slate-900">Status</p>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        company.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {company.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <CalendarIcon className="w-6 h-6 text-black" />
                  <div>
                    <p className="text-sm font-bold text-slate-900">
                      Created At
                    </p>
                    <p className="text-slate-800 font-medium">
                      {company.created_at
                        ? new Date(company.created_at).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )
                        : ""}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {editMode && (
          <div className="flex gap-2 mt-6 justify-center">
            <button
              className="px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={async (e) => {
                e.preventDefault();
                setEditLoading(true);
                setEditError("");
                try {
                  const res = await fetch(
                    `${environment.apiUrl}/company/${companyId}`,
                    {
                      method: "PUT",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${getToken()}`,
                      },
                      body: JSON.stringify({
                        ...editForm,
                        emr: Array.isArray(editForm.emr) ? editForm.emr : [],
                        is_active: editForm.is_active,
                      }),
                    }
                  );
                  if (!res.ok) throw new Error("Failed to update company");
                  const updated = await res.json();

                  // Handle backward compatibility: convert string EMR to array if needed
                  const emrValue = updated.emr || [];
                  const emrArray = Array.isArray(emrValue)
                    ? emrValue
                    : emrValue
                    ? [emrValue]
                    : [];

                  // Update company state with the response
                  setCompany((c) => ({ ...c, ...updated, emr: emrArray }));

                  // Update editForm to match the saved data
                  setEditForm({
                    name: updated.name || "",
                    industry: updated.industry || "",
                    emr: emrArray,
                    address: updated.address || "",
                    is_active:
                      typeof updated.is_active === "boolean"
                        ? updated.is_active
                        : true,
                  });

                  setEditMode(false);
                } catch (err) {
                  setEditError("Failed to update company");
                } finally {
                  setEditLoading(false);
                }
              }}
              disabled={editLoading}
            >
              {editLoading ? "Saving..." : "Save"}
            </button>
            <button
              className="px-4 py-1 bg-gray-200 text-slate-700 rounded hover:bg-gray-300"
              onClick={() => {
                // Reset editForm to current company data when canceling
                const emrValue = company.emr || [];
                const emrArray = Array.isArray(emrValue)
                  ? emrValue
                  : emrValue
                  ? [emrValue]
                  : [];

                setEditForm({
                  name: company.name || "",
                  industry: company.industry || "",
                  emr: emrArray,
                  address: company.address || "",
                  is_active:
                    typeof company.is_active === "boolean"
                      ? company.is_active
                      : true,
                });
                setEditMode(false);
              }}
              disabled={editLoading}
            >
              Cancel
            </button>
          </div>
        )}
        {editError && (
          <div className="col-span-2 text-red-500 text-sm text-center">
            {editError}
          </div>
        )}
      </div>
      {/* Users Section */}
      <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50 rounded-xl shadow-lg p-6 border border-slate-200">
        <h3 className="text-2xl font-bold mb-6 text-slate-800 flex items-center gap-3">
          <UsersIcon className="w-8 h-8 text-black" />
          Users
        </h3>
        {company.users && company.users.length > 0 ? (
          <div className="overflow-x-auto rounded-xl">
            <table className="min-w-full text-sm border-separate border-spacing-0">
              <thead>
                <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 text-slate-700 border-b border-slate-200">
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Full Name
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Role
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    Active
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-800">
                    User Type
                  </th>
                </tr>
              </thead>
              <tbody>
                {company.users.map((u, idx) => (
                  <tr
                    key={u.id}
                    className={`${
                      idx % 2 === 0 ? "bg-white" : "bg-slate-50"
                    } hover:bg-blue-50 cursor-pointer transition-colors border-b border-slate-100`}
                    onClick={() => navigate(`/user/${u.id}`)}
                  >
                    <td className="px-6 py-4 text-left text-slate-900 font-medium align-middle">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                          {u.full_name ? u.full_name[0].toUpperCase() : "U"}
                        </div>
                        {u.full_name || "John Doe"}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-left text-slate-700 align-middle">
                      {u.email || "—"}
                    </td>
                    <td className="px-6 py-4 text-left align-middle">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {u.role_name || u.role_id || "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-left align-middle">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          u.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {u.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-left align-middle">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        {u.user_type
                          ? u.user_type.charAt(0).toUpperCase() +
                            u.user_type.slice(1)
                          : "Standard"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-slate-500">No users found for this company.</div>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register-success" element={<RegisterSuccess />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-otp" element={<VerifyOTP />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Layout pageTitle="Dashboard">
                <Dashboard />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/Clients"
          element={
            <PrivateRoute>
              <Layout pageTitle="Clients">
                <Clients />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/Clients/:clientId"
          element={
            <PrivateRoute>
              <Layout pageTitle="Client Details">
                <ClientDetail />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/sessions"
          element={
            <PrivateRoute>
              <Layout pageTitle="Sessions">
                <Sessions />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/sessions/:sessionId"
          element={
            <PrivateRoute>
              <Layout pageTitle="Session Details">
                <SessionDetail />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/users"
          element={
            <PrivateRoute>
              <Layout pageTitle="Users">
                <Users />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/companies"
          element={
            <PrivateRoute adminOnly>
              <Layout pageTitle="Companies">
                <Companies />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/companies/:companyId"
          element={
            <PrivateRoute adminOnly>
              <Layout pageTitle="Company Details">
                <CompanyDetails />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <PrivateRoute>
              <Layout pageTitle="Reports">
                <Reports />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/emr-types"
          element={
            <PrivateRoute>
              <Layout pageTitle="EMR Types">
                <EMRTypes />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/emr-types/:id"
          element={
            <PrivateRoute>
              <Layout pageTitle="EMR Type Details">
                <EMRTypeDetails />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <Layout pageTitle="Settings">
                <Settings />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/modality/:modalityId"
          element={
            <PrivateRoute>
              <Layout pageTitle="Modality Details">
                <ModalityDetail />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/activity/:activityId"
          element={
            <PrivateRoute>
              <Layout pageTitle="Activity Details">
                <ActivityDetail />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/user/:userId"
          element={
            <PrivateRoute>
              <Layout pageTitle="Profile">
                <Profile />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <Layout pageTitle="Profile">
                <Profile />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
